import { Paper, Typography, Stack, Button, Avatar, Divider, Box, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import SimpleBackdrop from '../../../../BackDrop/BackDrop';
import { EditExpense } from '../ManualExpenseModals/EditExpense';
import { CheckPermissions } from '../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions';
import { useDispatch, useSelector } from 'react-redux';
import { selectUserData, selectUserToken, setDisplayErrorSnackBar } from '../../../../../features/userSlice';
import { ManualExpenseEndPointLogic } from '../../../../../logic/Finance/ProviderExpense/ManualExpenseEndpointLogic';
import CommentsAndFiles from '../../../../Discussion/CommentsAndFiles';

const ManualExpenseDetails = () => {
  const userData = useSelector(selectUserData);
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const expenseShareUid = searchParams.get("expenseShareUid");
  const selected = searchParams.get("selected");
  const [, setIsThisEdit] = useState(false);
  const [, setDeleteSuccess] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const allTrafficSources = useSelector((state: any) => state.manualExpense.allTrafficSources);

  const expenseData = useMemo(() => ({
    expenseName: searchParams.get("expenseName"),
    websiteName: searchParams.get("websiteName"),
    trafficSourceUid: searchParams.get("trafficSourceUid"),
    date: searchParams.get("date"),
    clicks: searchParams.get("clicks"),
    impressions: searchParams.get("impressions"),
    expenses: searchParams.get("expenses"),
    createdBy: searchParams.get("createdBy")
  }), [searchParams]);

  const isDataComplete = useMemo(() => {
    return Object.values(expenseData).every(value => value !== null && value !== '');
  }, [expenseData]);

  const getTrafficSourceName = useMemo(() => {
    if (!allTrafficSources || !Array.isArray(allTrafficSources) || !expenseData.trafficSourceUid) {
      return expenseData.trafficSourceUid || 'N/A';
    }
    
    const matchedSource = allTrafficSources.find((source: any) => 
      source.shared_uid === expenseData.trafficSourceUid
    );
    
    return matchedSource ? matchedSource.traffic_source_name : expenseData.trafficSourceUid;
  }, [allTrafficSources, expenseData.trafficSourceUid]);

  useEffect(() => {
    const fetchTrafficSources = async () => {
      if (oauthTokenHeader) {
        try {
          await ManualExpenseEndPointLogic.getTrafficSource(oauthTokenHeader);
        } catch (error) {
          console.error('Error fetching traffic sources:', error);
        }
      }
      // Add a small delay to prevent flickering and allow data to settle
      const timer = setTimeout(() => {
        setLoading(false);
      }, 100);
      return () => clearTimeout(timer);
    };
    fetchTrafficSources();
  }, [oauthTokenHeader]);

  const handleEditClick = (_note: any) => {
    setIsThisEdit(true);
    setIsDialogOpen(true);
    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams);
      updatedParams.set("edit", "true");
      return updatedParams;
    });
  }

  const hasEditManualExpansesPermission = CheckPermissions(
    userData?.data?.products?.finance,
    "providers_expenses",
    "edit_manual_expenses"
  );

  const hasDeleteManualExpansesPermission = CheckPermissions(
    userData?.data?.products?.finance,
    "providers_expenses",
    "delete_manual_expenses"
  );

  const handleDeleteExpense = async () => {
    setDeleteConfirmOpen(false); // Close the confirmation dialog
    setLoading(true);
    try {
      await ManualExpenseEndPointLogic.deleteExpanse(oauthTokenHeader, expenseShareUid, setDeleteSuccess);
      if (oauthTokenHeader) {
        await ManualExpenseEndPointLogic.getManualExpenseData(oauthTokenHeader, dispatch);
      } else {
        dispatch(setDisplayErrorSnackBar({ message: "Error while fetching data. Please try again later.", type: "error", }));
      }
    } catch (error: any) {
      dispatch(setDisplayErrorSnackBar({ message: "Error: " + error?.message || "An error occurred while deleting the expense.", type: "error", }));
    } finally {
      setLoading(false);
      if (selected && selected.length > 0) {
        navigate(`/finance/providers_expenses/manual_expenses?selected=${selected}`);
      } else {
        navigate("/finance/providers_expenses/manual_expenses");
      }
    }
  }

  return (
    <>
      {loading || !isDataComplete ? (
        <SimpleBackdrop />
      ) : (
        <>
          <Box sx={{ width: "100%", marginTop: 4 }}>
            <Paper elevation={3} sx={{ p: 2, borderRadius: 2, width: "100%" }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{ flex: 1 }}
                >
                  <Typography variant="h6">
                    {expenseData.expenseName &&
                    expenseData.expenseName.length > 50
                      ? expenseData.expenseName.slice(0, 50) + "..."
                      : expenseData.expenseName || ""}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                  {hasDeleteManualExpansesPermission ? (
                    <Button
                      color="secondary"
                      data-tracker="103"
                      onClick={() => setDeleteConfirmOpen(true)}
                      size="small"
                      sx={{ textTransform: "none" }}
                    >
                      <DeleteIcon />
                    </Button>
                  ) : (
                    <DeleteIcon color="disabled" />
                  )}
                  {hasEditManualExpansesPermission ? (
                    <Button
                      variant="contained"
                      color="secondary"
                      data-tracker="425"
                      endIcon={<EditIcon />}
                      sx={{ textTransform: "none" }}
                      onClick={handleEditClick}
                    >
                      EDIT EXPENSE
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="secondary"
                      data-tracker="425"
                      endIcon={<EditIcon />}
                      sx={{ textTransform: "none" }}
                      disabled
                    >
                      EDIT EXPENSE
                    </Button>
                  )}
                </Stack>
              </Stack>
              <Stack
                direction="row"
                spacing={3}
                flexWrap="wrap"
                mb={2}
                alignItems="center"
              >
                <Typography
                  variant="body2"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <span style={{ fontWeight: 500 }}>Source: </span>{" "}
                  {getTrafficSourceName}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <span style={{ fontWeight: 500 }}>Clicks: </span>{" "}
                  {expenseData.clicks || "N/A"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <span style={{ fontWeight: 500 }}>Impressions: </span>{" "}
                  {expenseData.impressions || "N/A"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <span style={{ fontWeight: 500 }}>Expenses: </span>{" "}
                  {expenseData.expenses || "N/A"}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                spacing={3}
                flexWrap="wrap"
                mb={2}
                alignItems="center"
              >
                <Typography
                  variant="body2"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <span style={{ fontWeight: 500 }}>Related Asset: </span>{" "}
                  {expenseData.websiteName
                    ? expenseData.websiteName.charAt(0).toUpperCase() +
                      expenseData.websiteName.slice(1)
                    : ""}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2">
                    <span style={{ fontWeight: 500 }}>Created By:</span>
                  </Typography>
                  <Avatar sx={{ width: 24, height: 24 }}>
                    {expenseData.createdBy?.charAt(0)?.toUpperCase()}
                  </Avatar>
                  <Typography variant="body2">
                    {expenseData.createdBy}
                  </Typography>
                </Stack>
                <Typography
                  variant="body2"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <span style={{ fontWeight: 500 }}>Date: </span>{" "}
                  {expenseData.date
                    ? new Date(expenseData.date).toLocaleDateString("en-GB")
                    : ""}
                </Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                {expenseData.expenseName}
              </Typography>
            </Paper>
          </Box>
          <CommentsAndFiles
            objectType="manual_adjustment_expenses"
            referenceModule="Finance Manual Expense"
            moduleType="manualExpense"
            objectSharedUid={expenseShareUid}
            postCommentTrackID="208"
            reactionTrackID="463"
            uploadFileTrackID="464"
            downloadFileTrackID="465"
            previewFileTrackID="520"
          />
          <EditExpense
            open={isDialogOpen}
            handleClose={() => setIsDialogOpen(false)}
            expenseData={expenseData}
          />
          <Dialog
            open={deleteConfirmOpen}
            aria-labelledby="delete-dialog-title"
            aria-describedby="delete-dialog-description"
          >
            <DialogTitle id="delete-dialog-title">
              Delete Activity Note
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="delete-dialog-description">
                Are you sure you want to delete this note? This action cannot be
                undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setDeleteConfirmOpen(false)}
                color="secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteExpense}
                color="secondary"
                variant="contained"
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </>
  );
}

export default ManualExpenseDetails
